import React, { useEffect, useState, useRef } from 'react';
import useAuthStore from '../store/useAuthStore';
import useInboxStore from '../store/useInboxStore';
import { playMessageSound } from '../utils/sounds';
import { initSocket, getSocket } from '../services/socket';
import api from '../services/api';

export default function InboxPage() {
  const { user, accessToken } = useAuthStore();
  const { setChatOpen, syncUnread, markRead } = useInboxStore();
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const typingTimeout = useRef(null);

  // Ensure socket is connected (global singleton from NotificationListener)
  useEffect(() => {
    initSocket(accessToken);
  }, [accessToken]);

  // Fetch conversations + sync unread badge
  useEffect(() => {
    api.get('/messages/conversations')
      .then(r => {
        setConversations(r.data);
        setLoading(false);
        syncUnread(r.data, user?.id);
      })
      .catch(() => setLoading(false));
  }, []);

  // Hide bottom nav when in active chat; restore on unmount
  useEffect(() => {
    setChatOpen(!!activeConv);
    return () => setChatOpen(false);
  }, [activeConv, setChatOpen]);

  // Listen for new messages
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    const handleNewMessage = (msg) => {
      const isFromOther = msg.senderId !== user?.id;
      if (msg.conversationId === activeConv?.id) {
        setMessages(prev => [...prev, msg]);
        scrollToBottom();
        if (isFromOther) playMessageSound();
      }
      // Update conversation preview in list
      setConversations(prev => prev.map(c =>
        c.id === msg.conversationId ? { ...c, messages: [msg] } : c
      ));
    };
    socket.on('new_message', handleNewMessage);
    socket.on('user_typing', ({ userId }) => {
      if (userId !== user?.id) setIsTyping(true);
    });
    socket.on('user_stop_typing', () => setIsTyping(false));
    return () => {
      socket.off('new_message', handleNewMessage);
      socket.off('user_typing');
      socket.off('user_stop_typing');
    };
  }, [activeConv, user]);

  const openConversation = async (conv) => {
    setActiveConv(conv);
    markRead(conv.id);
    getSocket()?.emit('join_conversation', conv.id);
    const { data } = await api.get(`/messages/conversations/${conv.id}`);
    setMessages(data);
    setTimeout(scrollToBottom, 100);
  };

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

  const handleSend = async (e) => {
    e.preventDefault();
    if (!text.trim() || !activeConv) return;
    const receiverId = activeConv.participants.find(p => p.userId !== user?.id)?.userId;
    try {
      const { data } = await api.post(`/messages/conversations/${activeConv.id}/send`, {
        content: text,
        receiverId
      });
      setMessages(prev => [...prev, data]);
      getSocket()?.emit('send_message', { ...data, conversationId: activeConv.id });
      setText('');
      scrollToBottom();
    } catch {}
  };

  const handleTyping = (e) => {
    setText(e.target.value);
    getSocket()?.emit('typing', { conversationId: activeConv?.id });
    clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => {
      getSocket()?.emit('stop_typing', { conversationId: activeConv?.id });
    }, 1500);
  };

  const getOtherParticipant = (conv) =>
    conv.participants?.find(p => p.user?.id !== user?.id)?.user;

  return (
    <div className="h-screen bg-dark-bg flex flex-col pb-16">
      {/* Header */}
      <div className="px-4 pt-12 pb-3 border-b border-dark-border flex-shrink-0">
        <div className="text-2xl font-bold text-dark-text" style={{ letterSpacing: '-0.5px' }}>
          {activeConv ? (
            <div className="flex items-center gap-3">
              <button onClick={() => setActiveConv(null)} className="text-dark-subtext">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6"/>
                </svg>
              </button>
              <div>
                <p className="text-base font-semibold text-dark-text leading-tight">
                  {getOtherParticipant(activeConv)?.fullName || 'Chat'}
                </p>
                {(() => {
                  const lastMsg = activeConv.messages?.[0];
                  const other = getOtherParticipant(activeConv);
                  if (!lastMsg || !other) return null;
                  const isOtherMsg = lastMsg.senderId !== user?.id;
                  if (!isOtherMsg) return <p className="text-xs text-dark-subtext">Last seen {new Date(lastMsg.createdAt).toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })}</p>;
                  return <p className="text-xs text-green-400">Active recently</p>;
                })()}
              </div>
            </div>
          ) : 'Inbox'}
        </div>
      </div>

      {!activeConv ? (
        /* Conversation list */
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex flex-col gap-0">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-4 border-b border-dark-border">
                  <div className="w-12 h-12 rounded-full bg-dark-muted animate-pulse" />
                  <div className="flex-1">
                    <div className="h-3 bg-dark-muted rounded w-32 mb-2 animate-pulse" />
                    <div className="h-2 bg-dark-muted rounded w-48 animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : conversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full py-16">
              <p className="text-4xl mb-3">💬</p>
              <p className="text-dark-text font-semibold mb-1">No messages yet</p>
              <p className="text-dark-subtext text-sm">Start a conversation by booking a service</p>
            </div>
          ) : (
            conversations.map(conv => {
              const other = getOtherParticipant(conv);
              const lastMsg = conv.messages?.[0];
              return (
                <button
                  key={conv.id}
                  onClick={() => openConversation(conv)}
                  className="w-full flex items-center gap-3 px-4 py-4 border-b border-dark-border hover:bg-dark-surface/50 transition-colors"
                >
                  <div className="w-12 h-12 rounded-full bg-brand-gradient flex items-center justify-center text-white font-bold flex-shrink-0 overflow-hidden">
                    {other?.avatar
                      ? <img src={other.avatar} className="w-full h-full object-cover" alt="" />
                      : (other?.fullName?.[0] || '?')}
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-dark-text text-sm font-semibold">{other?.fullName || 'Unknown'}</span>
                      {lastMsg && (
                        <span className="text-dark-subtext text-xs">
                          {new Date(lastMsg.createdAt).toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                    </div>
                    {conv.booking?.service?.title && (
                      <p className="text-brand-purple text-xs mb-0.5">{conv.booking.service.title}</p>
                    )}
                    {lastMsg && (
                      <p className="text-dark-subtext text-xs truncate">{lastMsg.content}</p>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
      ) : (
        /* Chat view */
        <>
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map(msg => {
              const isMe = msg.senderId === user?.id || msg.sender?.id === user?.id;
              return (
                <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                  {!isMe && (
                    <div className="w-7 h-7 rounded-full bg-brand-gradient flex items-center justify-center text-white text-xs font-bold mr-2 flex-shrink-0 mt-1">
                      {msg.sender?.fullName?.[0] || '?'}
                    </div>
                  )}
                  <div className={`max-w-[72%] px-4 py-2.5 rounded-2xl ${
                    isMe
                      ? 'bg-brand-gradient text-white rounded-br-sm'
                      : 'bg-dark-surface border border-dark-border text-dark-text rounded-bl-sm'
                  }`}>
                    <p className="text-sm leading-relaxed">{msg.content}</p>
                    <p className={`text-xs mt-1 ${isMe ? 'text-white/60' : 'text-dark-subtext'}`}>
                      {new Date(msg.createdAt).toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              );
            })}
            {isTyping && (
              <div className="flex items-center gap-2">
                <div className="bg-dark-surface border border-dark-border rounded-2xl px-4 py-2.5">
                  <div className="flex gap-1">
                    {[0, 1, 2].map(i => (
                      <div
                        key={i}
                        className="w-1.5 h-1.5 rounded-full bg-dark-subtext animate-bounce"
                        style={{ animationDelay: `${i * 0.15}s` }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form
            onSubmit={handleSend}
            className="px-4 py-3 border-t border-dark-border flex items-center gap-3 flex-shrink-0 bg-dark-bg"
          >
            <input
              type="text"
              value={text}
              onChange={handleTyping}
              placeholder="Type a message..."
              className="flex-1 bg-dark-surface border border-dark-border rounded-2xl px-4 py-3 text-dark-text text-sm outline-none focus:border-brand-purple"
            />
            <button
              type="submit"
              disabled={!text.trim()}
              className="w-11 h-11 rounded-full bg-brand-gradient flex items-center justify-center flex-shrink-0 disabled:opacity-50"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <line x1="22" y1="2" x2="11" y2="13"/>
                <polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            </button>
          </form>
        </>
      )}
    </div>
  );
}
